import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { api, Order } from '../services/api';
import { useCart } from '../contexts/CartContext';
import './OrderSuccess.css';
import { proxyBucketUrl, handleImageError } from '../utils/imageUtils';

const OrderSuccess: React.FC = () => {
  const { orderNumber } = useParams<{ orderNumber: string }>();
  const [searchParams] = useSearchParams();
  const { clearCart } = useCart();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [paymentDeclined, setPaymentDeclined] = useState(false);

  useEffect(() => {
    const loadOrder = async () => {
      if (!orderNumber) {
        setError('Order number is required');
        setLoading(false);
        return;
      }

      try {
        // Returning from the NBG Pay Hosted Payment Page: confirm the real
        // outcome server-side before showing anything to the customer. The
        // cart was deliberately left untouched until now, in case the
        // customer cancelled on NBG Pay's page instead of completing payment.
        if (searchParams.get('src') === 'nbgpay') {
          const { paymentStatus } = await api.confirmPayment(orderNumber);
          if (paymentStatus === 'FAILED') {
            setPaymentDeclined(true);
          } else {
            await clearCart();
          }
        }

        const orderData = await api.getOrder(orderNumber);
        setOrder(orderData);
      } catch (err: any) {
        setError(err.message || 'Failed to load order');
        console.error('Failed to load order:', err);
      } finally {
        setLoading(false);
      }
    };

    loadOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderNumber, searchParams]);

  if (loading) {
    return (
      <div className="order-success-page">
        <div className="order-container">
          <div className="order-loading">
            <div className="loading-spinner"></div>
            <p>Loading order details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="order-success-page">
        <div className="order-container">
          <div className="order-error">
            <div className="error-icon">❌</div>
            <h2>Order Not Found</h2>
            <p>{error || 'The order you\'re looking for doesn\'t exist.'}</p>
            <Link to="/shop" className="continue-btn">Continue Shopping</Link>
          </div>
        </div>
      </div>
    );
  }

  if (paymentDeclined) {
    return (
      <div className="order-success-page">
        <div className="order-container">
          <div className="order-error">
            <div className="error-icon">❌</div>
            <h2>Payment Not Completed</h2>
            <p>Your payment was declined or cancelled. Your order (#{order.orderNumber}) has not been charged.</p>
            <Link to="/checkout" className="continue-btn">Try Again</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="order-success-page">
      <div className="order-container">
        <div className="success-header">
          <div className="success-icon">✅</div>
          <h1>Order Confirmed!</h1>
          <p className="success-message">
            Thank you for your purchase! Your order has been successfully placed.
          </p>
        </div>

        <div className="order-details">
          <div className="order-info">
            <h2>Order Information</h2>
            <div className="info-grid">
              <div className="info-item">
                <span className="label">Order Number:</span>
                <span className="value">{order.orderNumber}</span>
              </div>
              <div className="info-item">
                <span className="label">Order Date:</span>
                <span className="value">{new Date(order.createdAt).toLocaleDateString()}</span>
              </div>
              <div className="info-item">
                <span className="label">Status:</span>
                <span className={`status status-${order.status.toLowerCase()}`}>
                  {order.status}
                </span>
              </div>
              <div className="info-item">
                <span className="label">Payment Method:</span>
                <span className="value">
                  {order.paymentMethod === 'CASH' ? '💵 Cash on Delivery' : 
                   order.paymentMethod === 'CARD' ? '💳 Credit Card' :
                   order.paymentMethod === 'PAYPAL' ? '📧 PayPal' :
                   order.paymentMethod === 'BANK' ? '🏦 Bank Transfer' : order.paymentMethod}
                </span>
              </div>
              <div className="info-item">
                <span className="label">Total Amount:</span>
                <span className="value amount">€{Number(order.totalAmount).toFixed(2)}</span>
              </div>
            </div>
          </div>

          <div className="order-items">
            <h3>Order Items</h3>
            <div className="items-list">
              {order.items?.map(item => (
                <div key={item.id} className="order-item">
                  <div className="item-image">
                    <img 
                      src={proxyBucketUrl(item.product?.images?.[0]) || '/products/placeholder.svg'} 
                      alt={item.product?.name || 'Product'} 
                      onError={handleImageError}
                    />
                  </div>
                  <div className="item-details">
                    <h4>{item.product?.name}</h4>
                    <p className="item-category">{item.product?.category?.name}</p>
                    {item.size && <p className="item-size">Size: {item.size}</p>}
                    <div className="item-features">
                      <span className="feature-tag">Authentic</span>
                      <span className="feature-tag">Unique</span>
                    </div>
                  </div>
                  <div className="item-price">
                    <span className="quantity">Qty: {item.quantity}</span>
                    <span className="price">€{Number(item.product?.price || 0).toFixed(2)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="shipping-info">
            <h3>Shipping Information</h3>
            <div className="shipping-address">
              <p>{order.shippingAddress}</p>
            </div>
            <div className="shipping-note">
              <div className="note-icon">📦</div>
              <div className="note-content">
                <h4>What happens next?</h4>
                <ul>
                  <li>You'll receive a confirmation email shortly</li>
                  <li>We'll prepare your vintage pieces with care</li>
                  <li>You'll get tracking information once your order ships</li>
                  <li>Estimated delivery: 3-5 business days</li>
                  {order.paymentMethod === 'CASH' && (
                    <li className="cash-note">💵 <strong>Cash Payment:</strong> Please have the exact amount (€{Number(order.totalAmount).toFixed(2)}) ready for the delivery person</li>
                  )}
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="success-actions">
          <Link to="/shop" className="continue-shopping-btn">
            Continue Shopping
          </Link>
          <Link to="/account?tab=orders" className="view-orders-btn">
            View All Orders
          </Link>
        </div>

        <div className="support-info">
          <div className="support-icon">💬</div>
          <div className="support-content">
            <h4>Need Help?</h4>
            <p>If you have any questions about your order, please don't hesitate to contact us.</p>
            <div className="contact-options">
              <span>📧 support@ecofashion.com</span>
              <span>📞 +30 210 123 4567</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderSuccess;